import { FormlyFieldConfig } from "@ngx-formly/core";
import { DEVICE_STATUS_VALUES } from "@cosmos/shared";

const DEVICE_TYPES = ['DataAcquisition', 'LoraGateway', 'MiniPC', 'SmartSensor'];

// Fields shared by more than one (but not all) device types are declared once here
// and gated with an array-membership hide check, instead of being duplicated per type.
const DEVICE_PROFILE_TYPES = ['DataAcquisition', 'SmartSensor'];
const MAC_TYPES = ['LoraGateway', 'MiniPC'];

export class DeviceForm {

    commonFields: FormlyFieldConfig[];
    fields: FormlyFieldConfig[];

    actionText = 'devices.create.button';

    constructor(translateService, dataCompanies, dataProjects, dataProfiles, dataSkus, user, config?) {

        const profiles: {value: string | null, label: string}[] = [];
        for(const profile of dataProfiles) profiles.push({value: profile['_id'], label: profile['name'] });

        const companies: {value: string | null, label: string}[] = [];
        for(const company of dataCompanies) companies.push({value: company['_id'], label: company['name'] });

        const projects: {value: string | null, label: string}[] = [{value: null, label: '-'}];
        for(const project of dataProjects) projects.push({value: project['_id'], label: project['name'] });

        const skus: {value: string | null, label: string}[] = [];
        for(const sku of dataSkus) skus.push({value: sku['ref'], label: sku['productName'] });

        const deviceTypes = DEVICE_TYPES.map(t => ({ value: t, label: translateService.instant('devices.types.' + t) }));

        const isSuperadmin = user.roles.includes('superadmin');

        this.commonFields = [
            {
                key: 'deviceType',
                type: 'select',
                wrappers: ['default-wrapper'],
                props: {
                    required: true,
                    options: deviceTypes
                },
                expressions: {
                    'props.label': translateService.stream('fields.deviceType')
                }
            },
            {
                key: 'name',
                type: 'input',
                wrappers: ['default-wrapper'],
                props: {
                    required: true
                },
                expressions: {
                    'props.label': translateService.stream('fields.name')
                }
            },
            {
                key: 'ref',
                type: 'input',
                hide: !isSuperadmin,
                wrappers: ['default-wrapper'],
                props: {
                    required: true
                },
                expressions: {
                    'props.label': translateService.stream('fields.ref')
                }
            },
            {
                key: 'sku',
                type: 'select',
                hide: !isSuperadmin,
                wrappers: ['default-wrapper'],
                props: {
                    required: false, // switch to true once skus are available in the database
                    options: skus
                },
                expressions: {
                    'props.label': translateService.stream('fields.sku')
                }
            },
            {
                key: 'manufacturerRef',
                type: 'input',
                hide: !isSuperadmin,
                wrappers: ['default-wrapper'],
                props: {
                    required: false
                },
                expressions: {
                    'props.label': translateService.stream('fields.manufacturerRef')
                }
            },
            {
                key: 'company',
                type: 'select',
                hide: !isSuperadmin,
                wrappers: ['default-wrapper'],
                props: {
                    required: true,
                    options: companies
                },
                expressions: {
                    'props.label': translateService.stream('fields.company')
                }
            },
            {
                key: 'project',
                type: 'select',
                wrappers: ['default-wrapper'],
                hide: !isSuperadmin,
                props: {
                    required: false,
                    options: projects,
                },
                expressions: {
                    'props.label': translateService.stream('fields.project'),
                }
            },
            // TYPE: DataAcquisition
            {
                key: 'loramac',
                type: 'input',
                hide: !isSuperadmin,
                wrappers: ['default-wrapper'],
                props: {
                    required: true
                },
                expressions: {
                    'props.label': translateService.stream('fields.loramac'),
                    hide: (field) => !isSuperadmin || field.model?.deviceType !== 'DataAcquisition',
                }
            },
            // TYPE: DataAcquisition, SmartSensor
            {
                key: 'deviceProfile',
                type: 'select',
                hide: !isSuperadmin,
                wrappers: ['default-wrapper'],
                props: {
                    required: true,
                    options: profiles
                },
                expressions: {
                    'props.label': translateService.stream('fields.deviceProfile'),
                    hide: (field) => !isSuperadmin || !DEVICE_PROFILE_TYPES.includes(field.model?.deviceType),
                }
            },
            // TYPE: LoraGateway, MiniPC
            {
                key: 'mac',
                type: 'input',
                hide: !isSuperadmin,
                wrappers: ['default-wrapper'],
                props: {
                    required: true
                },
                expressions: {
                    'props.label': translateService.stream('fields.mac'),
                    hide: (field) => !isSuperadmin || !MAC_TYPES.includes(field.model?.deviceType),
                }
            },
            // TYPE: SmartSensor — 32 hex char hardware UID reported by the sensor firmware
            {
                key: 'uid',
                type: 'input',
                hide: !isSuperadmin,
                wrappers: ['default-wrapper'],
                props: {
                    required: true
                },
                expressions: {
                    'props.label': translateService.stream('fields.uid'),
                    hide: (field) => !isSuperadmin || field.model?.deviceType !== 'SmartSensor',
                }
            },
            // TYPE: LoraGateway
            {
                key: 'vpn',
                type: 'input',
                hide: !isSuperadmin,
                wrappers: ['default-wrapper'],
                props: {
                    required: false,
                },
                expressions: {
                    'props.label': translateService.stream('fields.vpn'),
                    hide: (field) => !isSuperadmin || field.model?.deviceType !== 'LoraGateway',
                }
            },
        ]

        this.fields = this.commonFields;
    }
}

export class EditDeviceForm {

    commonFields: FormlyFieldConfig[];
    fields: FormlyFieldConfig[];

    actionText = 'button.save';

    constructor(translateService, device, dataCompanies, dataProjects, dataProfiles, dataSkus, user, config?) {

        const profiles: {value: string | null, label: string}[] = [];
        for(const profile of dataProfiles) profiles.push({value: profile['_id'], label: profile['name'] });

        const companies: {value: string | null, label: string}[] = [];
        for(const company of dataCompanies) companies.push({value: company['_id'], label: company['name'] });

        const projects: {value: string | null, label: string}[] = [{value: null, label: '-'}];
        for(const project of dataProjects) projects.push({value: project['_id'], label: project['name'] });

        const skus: {value: string | null, label: string}[] = [];
        for(const sku of dataSkus) skus.push({value: sku['ref'], label: sku['productName'] });

        const statuses = DEVICE_STATUS_VALUES.map(s => ({ value: s, label: translateService.instant('constants.device.status.' + s) }));

        const isSuperadmin = user.roles.includes('superadmin');
        const deviceType = device.deviceType;

        this.commonFields = [
            {
                key: 'name',
                type: 'input',
                defaultValue: device.name,
                wrappers: ['default-wrapper'],
                props: {
                    required: true
                },
                expressions: {
                    'props.label': translateService.stream('fields.name')
                }
            },
            {
                key: 'ref',
                type: 'input',
                hide: !isSuperadmin,
                defaultValue: device.ref,
                wrappers: ['default-wrapper'],
                props: {
                    required: true
                },
                expressions: {
                    'props.label': translateService.stream('fields.ref')
                }
            },
            {
                key: 'sku',
                type: 'select',
                wrappers: ['default-wrapper'],
                hide: !isSuperadmin,
                defaultValue: device.sku,
                props: {
                    required: false, // switch to true once skus are available in the database
                    options: skus
                },
                expressions: {
                    'props.label': translateService.stream('fields.sku')
                }
            },
            {
                key: 'manufacturerRef',
                type: 'input',
                hide: !isSuperadmin,
                defaultValue: device.manufacturerRef,
                wrappers: ['default-wrapper'],
                props: {
                    required: false
                },
                expressions: {
                    'props.label': translateService.stream('fields.manufacturerRef')
                }
            },
            {
                key: 'company',
                type: 'select',
                hide: !isSuperadmin,
                defaultValue: device.company._id,
                wrappers: ['default-wrapper'],
                props: {
                    required: true,
                    options: companies
                },
                expressions: {
                    'props.label': translateService.stream('fields.company')
                }
            },
            {
                key: 'project',
                type: 'select',
                defaultValue: (device.project?._id)?device.project._id:null,
                wrappers: ['default-wrapper'],
                props: {
                    required: false,
                    options: projects,
                },
                expressions: {
                    'props.label': translateService.stream('fields.project'),
                }
            },
            {
                key: 'status',
                type: 'select',
                hide: !isSuperadmin,
                defaultValue: device.status,
                wrappers: ['default-wrapper'],
                props: {
                    required: true,
                    options: statuses
                },
                expressions: {
                    'props.label': translateService.stream('fields.status')
                }
            },
            // TYPE: DataAcquisition
            {
                key: 'loramac',
                type: 'input',
                hide: !isSuperadmin || deviceType !== 'DataAcquisition',
                defaultValue: device.loramac,
                wrappers: ['default-wrapper'],
                props: {
                    required: true
                },
                expressions: {
                    'props.label': translateService.stream('fields.loramac')
                }
            },
            // TYPE: DataAcquisition, SmartSensor
            {
                key: 'deviceProfile',
                type: 'select',
                hide: !isSuperadmin || !DEVICE_PROFILE_TYPES.includes(deviceType),
                wrappers: ['default-wrapper'],
                defaultValue: device.deviceProfile?._id ?? device.deviceProfile,
                props: {
                    required: true,
                    options: profiles
                },
                expressions: {
                    'props.label': translateService.stream('fields.deviceProfile')
                }
            },
            // TYPE: LoraGateway, MiniPC
            {
                key: 'mac',
                type: 'input',
                hide: !isSuperadmin || !MAC_TYPES.includes(deviceType),
                defaultValue: device.mac,
                wrappers: ['default-wrapper'],
                props: {
                    required: true
                },
                expressions: {
                    'props.label': translateService.stream('fields.mac')
                }
            },
            // TYPE: SmartSensor — 32 hex char hardware UID reported by the sensor firmware
            {
                key: 'uid',
                type: 'input',
                hide: !isSuperadmin || deviceType !== 'SmartSensor',
                defaultValue: device.uid,
                wrappers: ['default-wrapper'],
                props: {
                    required: true
                },
                expressions: {
                    'props.label': translateService.stream('fields.uid')
                }
            },
            // TYPE: LoraGateway
            {
                key: 'vpn',
                type: 'input',
                hide: !isSuperadmin || deviceType !== 'LoraGateway',
                defaultValue: device.vpn,
                wrappers: ['default-wrapper'],
                props: {
                    required: false,
                },
                expressions: {
                    'props.label': translateService.stream('fields.vpn')
                }
            },
            // TYPE: LoraGateway — runtime detail, unknown at creation time; defaults to 'v4' on the backend
            {
                key: 'version',
                type: 'input',
                hide: !isSuperadmin || deviceType !== 'LoraGateway',
                defaultValue: device.version,
                wrappers: ['default-wrapper'],
                props: {
                    required: false,
                },
                expressions: {
                    'props.label': translateService.stream('fields.version')
                }
            },
            // TYPE: SmartSensor — runtime detail, unknown at creation time; not superadmin-gated
            {
                key: 'busAddress',
                type: 'input',
                hide: deviceType !== 'SmartSensor',
                defaultValue: device.busAddress,
                wrappers: ['default-wrapper'],
                props: {
                    required: false,
                    type: 'number',
                },
                expressions: {
                    'props.label': translateService.stream('fields.busAddress')
                }
            },
        ]

        this.fields = this.commonFields;
    }
}
